import { useMemo, isValidElement, JSX } from "react"
import { Tooltip, TooltipClasses, TooltipProps } from "@mui/material"
import { update, isObject, isArray, map, size, compact, keys, isEmpty } from "lodash"

import "./styles.scss"

function combineClasses(classes?: Partial<TooltipClasses>, variant?: string) {
  // This allows for screen specific styling
  const combinedClasses = classes || {}
  update(combinedClasses, "popper", (value) => `component_material_tooltip ${variant}${value ? ` ${value}` : ""}`)
  update(combinedClasses, "tooltip", (value) => `tooltip${value ? ` ${value}` : ""}`)
  return combinedClasses
}

function mapTitleToElements(titleContents: (string | JSX.Element)[]) {
  return <>{map(titleContents, (c, k) => (<div key={k} className={`${k}`}>{c}</div>))}</>
}

function formatTitleObject(titleObject: object) {
  if (size(keys(titleObject)) === 0) return ""
  // If this is a react element, use it as-is. See: https://react.dev/reference/react/isValidElement#reference
  if (isValidElement(titleObject)) {
    return titleObject
  }

  // [TODO:TS] How can we get here?
  // We specifically excluded arrays from calling this function,
  // so what are we mapping over?
  return mapTitleToElements(titleObject as string[])
}

function formatTitleArray(titleArray: (string | JSX.Element)[]) {
  if (size(titleArray) === 0) return ""
  return size(titleArray) === 1 ? titleArray[0] : mapTitleToElements(titleArray)
}

export default function OPTooltip({ title, classes, variant = "default", children, ...props }: OPTooltipProps) {
  const formattedTitle = useMemo((): JSX.Element | string => {
    let sanitizedTitleContent = null

    if (isObject(title) && !isArray(title)) {
      sanitizedTitleContent = formatTitleObject(title)
    } else if (isArray(title)) {
      sanitizedTitleContent = formatTitleArray(compact<string | JSX.Element>(title))
    } else {
      sanitizedTitleContent = title
    }

    return sanitizedTitleContent
  }, [title])

  if (isEmpty(formattedTitle)) {
    return children
  }

  return <Tooltip
    disableInteractive
    title={formattedTitle}
    classes={combineClasses(classes, variant)}
    {...props}
  >
    {children}
  </Tooltip>
}

interface OPTooltipProps extends TooltipProps {
  readonly title: JSX.Element | string | readonly (string | JSX.Element)[]
  readonly variant?: string
}
